<script lang="ts" setup>
import {
  fallbackAttachmentSizeMapping,
  FallbackAttachmentType,
  fallbackImageUrl,
  isAttachmentBlocked,
} from '@@/bits/attachments'
import { getDisplayAttributes } from '@@/bits/beethoven'
import { isElementOrChildOverflowingParent, resizeElementToFitParentWithScale } from '@@/bits/dom'
import { __ } from '@@/bits/intl'
import { isTweet as hasTweetAttachment } from '@@/bits/post_attachment'
import { postColorRgb, serializeRgbaStringToRgba } from '@@/bits/post_color'
import { pollSlideshowResizeScales } from '@@/bits/slideshow'
import { defineAsyncComponent } from '@@/bits/vue'
import { isSmallerThanDesktop, isSmallerThanDesktopBig } from '@@/bits/window'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { Poll, Post, PostColor } from '@@/types'
import { useSlideshowAttachments } from '@@/vuecomposables/slideshow_attachments'
import { LinkHelpers, type BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    post: Post
    darkMode: boolean
    /**  Full page means the attachment has the whole container to itself (no post text).
    This affects things like the height it can take (e.g. video should not take full height of page)
    and horizontal padding (e.g. the attachment needs its own horizontal padding)
   */
    isFullPage: boolean
    isCurrentlyViewed: boolean
    /* used for tweet attachment width */
    containerWidth: number
    /* used for poll attachment height */
    containerHeight?: number
  }>(),
  {
    containerHeight: undefined,
  },
)

// Components
const SurfaceAttachmentContentBlocked = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceAttachmentContentBlocked.vue'),
)
const SurfaceSlideshowAttachmentImage = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowAttachmentImage.vue'),
)
const SurfaceSlideshowAttachmentAudio = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowAttachmentAudio.vue'),
)
const SurfaceSlideshowAttachmentVideo = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowAttachmentVideo.vue'),
)
const SurfaceSlideshowAttachmentEmbed = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowAttachmentEmbed.vue'),
)
const SurfaceSlideshowCardFile = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSlideshowCardFile.vue'))
const SurfaceSlideshowCardLink = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSlideshowCardLink.vue'))
const SurfaceSlideshowCardWall = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceSlideshowCardWall.vue'))
const SurfaceSlideshowAttachmentTweet = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceSlideshowAttachmentTweet.vue'),
)
const SurfacePostPoll = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostPoll.vue'))

// Vue composables
const { addPostCidToLoadedAttachments } = useSlideshowAttachments()
const { fetchDisplayAttributesForPost, getLinkDisplayAttributesForPost, getAttachmentPropsFromPost } =
  useSurfaceAttachmentsStore()

// Stores
const { blockedContentProviders, user, isScreenshotMode } = storeToRefs(useSurfaceStore())

const { isSmallerThanTabletPortrait } = storeToRefs(useWindowSizeStore())

// =========================
// FORCE REMOUNT LOGIC
// =========================
const previousIsCurrentlyViewed = ref(props.isCurrentlyViewed)
const forceBackgroundReloadKey = ref(0)
/**
 * Remounts the component when the currently viewed state changes.

  * When a user is on a page with an attachment that plays audio, and they navigate to another page.
 * The component will still play audio in the background because of the preloading logic.
 *
 * This forces the component to be destroyed and remounted when it goes from viewed to not viewed.
 *
 * This is a "catch-all" to stop any audio from background attachments, which is more
 * straightfoward than trying to handle the multiple types of attachments and deal with iframe jank.
 *
 * @param currentlyViewing - A boolean indicating whether the component is currently being viewed.
 * @param previouslyViewing - A boolean indicating whether the component was previously being viewed.
 */
const remountComponentWhenCurrentlyViewing = (currentlyViewing: boolean, previouslyViewing: boolean) => {
  if (currentlyViewing && !previouslyViewing) {
    forceBackgroundReloadKey.value++
  }
  previousIsCurrentlyViewed.value = currentlyViewing
}

watch(
  () => props.isCurrentlyViewed,
  (newValue, oldValue) => {
    remountComponentWhenCurrentlyViewing(newValue, oldValue)
    updateListenerToRemoveUnintendedFocusIfAny()
  },
)

// =========================
// REMOVE UNINTENDED FOCUS
// =========================
/* After the attachment has loaded or when the post is being viewed,
   we start a loop to check every 50ms if there is an unintended focus on the attachment due to popups like Google signin prompt.
   If so, we remove the focus and focus the body or the previous navigation button element so user can continue keyboard navigation
   This removal of focus is done at most once everytime the user loads a slide,
   because the unintended content will load everytime a post with an embed that has the unintended content is reloaded.

   We also stop the loop after 8 seconds (to accomodate low end devices) to prevent it from running indefinitely

   We have the logic here at SurfaceSlideshowAttachment instead of SurfaceSlideshowAttachmentEmbed because
   the unmount lifecycle hook did not trigger as expected in the child component when the forceBackgroundReloadKey is changed
 */
const FOCUS_LISTENER_INTERVAL_TIMER = 50
const STOP_FOCUS_LISTENER_INTERVAL_TIMER = 8000
let intervalTimer: ReturnType<typeof setInterval> | undefined
let timeout: ReturnType<typeof setTimeout> | undefined
let lastFocusedElement: HTMLElement | null = null

const startListenerToRemoveUnintendedFocusIfAny = () => {
  intervalTimer = setInterval(() => {
    const focusedElement = document.activeElement as HTMLElement
    if (focusedElement.nodeName !== 'IFRAME') {
      lastFocusedElement = focusedElement
      return
    }

    // For a11y, we want to return focus to what the user was previously focused on before the iframe stole focus
    focusedElement.blur()
    removeListenerToRemoveUnintendedFocusIfAny()

    if (lastFocusedElement) {
      lastFocusedElement.focus()
    }
  }, FOCUS_LISTENER_INTERVAL_TIMER)

  timeout = setTimeout(() => {
    removeListenerToRemoveUnintendedFocusIfAny()
  }, STOP_FOCUS_LISTENER_INTERVAL_TIMER)
}

const removeListenerToRemoveUnintendedFocusIfAny = () => {
  intervalTimer && clearInterval(intervalTimer)
  timeout && clearTimeout(timeout)
}

const updateListenerToRemoveUnintendedFocusIfAny = () => {
  if (props.isCurrentlyViewed) {
    startListenerToRemoveUnintendedFocusIfAny()
  } else {
    removeListenerToRemoveUnintendedFocusIfAny()
  }
}

// =========================
// DISPLAY ATTRIBUTES
// =========================
const displayAttributes = ref<BeethovenDisplayAttributes | null>(null)
const customThumbnailUrl = computed(() => props.post.wish_content?.attachment_props?.custom_thumbnail_url)
const updateDisplayAttributes = async () => {
  displayAttributes.value = null
  fetchDisplayAttributesForPost(props.post.cid).then(async () => {
    let newDisplayAttrs: BeethovenDisplayAttributes | null = null
    newDisplayAttrs = getLinkDisplayAttributesForPost(props.post.cid)
    if (customThumbnailUrl.value) {
      const customThumbnailDisplayAttrs = await getDisplayAttributes(customThumbnailUrl.value)
      if (
        customThumbnailDisplayAttrs?.image_url &&
        customThumbnailDisplayAttrs?.image_height &&
        customThumbnailDisplayAttrs?.image_width &&
        newDisplayAttrs
      ) {
        // we only overwrite the display attributes for showing the custom thumbnail image
        // the rest of the display attributes will be the same as the original attachment link
        newDisplayAttrs.image_url = customThumbnailDisplayAttrs.image_url
        newDisplayAttrs.image_height = customThumbnailDisplayAttrs.image_height
        newDisplayAttrs.image_width = customThumbnailDisplayAttrs.image_width
      }
    }
    displayAttributes.value = newDisplayAttrs
  })
}

watch(
  () => props.post,
  () => {
    updateDisplayAttributes()
  },
)

const lowerCaseProviderName = computed(() => {
  return displayAttributes.value?.provider_name.toLowerCase()
})

const hasBlockedContent = computed(() => {
  return displayAttributes.value ? isAttachmentBlocked(displayAttributes.value, blockedContentProviders.value) : false
})

const isFile = computed(() => {
  return displayAttributes.value?.content_category === 'file'
})

const isImage = computed(() => {
  return displayAttributes.value?.content_category === 'photo'
})

const isAudio = computed(() => {
  return displayAttributes.value?.content_category === 'audio'
})

const isVideo = computed(() => {
  return displayAttributes.value?.content_category === 'video'
})

const isVideoPortrait = computed(() => {
  if (!displayAttributes.value || !displayAttributes.value.embed_height || !displayAttributes.value.embed_width)
    return false
  return displayAttributes.value.embed_height > displayAttributes.value.embed_width
})

const videoAttachmentContainerHeight = computed(() => {
  if (isVideoPortrait.value) {
    return props.isFullPage ? 'h-full' : 'h-full tablet-landscape:h-[79%] desktop-2xl:h-[90%]'
  }

  return props.isFullPage
    ? 'h-[26%] tablet-portrait:h-[42%] tablet-landscape:h-[79%] desktop-2xl:h-[90%]'
    : 'h-full tablet-landscape:h-[32%] desktop-2xl:h-[38%]'
})

enum ResizableVideoProvider {
  DAILYMOTION = 'dailymotion',
  LOOM = 'loom',
  VIMEO = 'vimeo',
  YOUTUBE = 'youtube',
}

const isResizableVideo = computed(() => {
  const providers = Object.values(ResizableVideoProvider).map((p) => p.toLowerCase())
  return lowerCaseProviderName.value ? providers.includes(lowerCaseProviderName.value) : false
})

const isYoutube = computed(() => {
  return lowerCaseProviderName.value === 'youtube'
})

const isTiktokVideo = computed(() => {
  /**
   * Nov. 28, 2024
   * TikTok does not provide proper size attributes
   * for us to handle the embeds.
   * For this reason, we will treat TikTok embeds as video only on mobile.
   */
  return lowerCaseProviderName.value === 'tiktok' && isSmallerThanTabletPortrait.value
})

const isUpload = computed(() => {
  return lowerCaseProviderName.value === 'padlet drive'
})

const isDocument = computed(() => {
  return displayAttributes.value?.content_category === 'document'
})

const isGoogleDoc = computed(() => {
  return displayAttributes.value && LinkHelpers.isGoogleDoc(displayAttributes.value)
})

const isArticle = computed(() => {
  return (
    displayAttributes.value?.content_category === 'page' && displayAttributes.value?.content_subcategory === 'article'
  )
})

const isHtml = computed(() => {
  return displayAttributes.value?.content_category === 'page' && displayAttributes.value?.content_subcategory === 'html'
})

const isPadletSlideshow = computed(() => {
  return displayAttributes.value && LinkHelpers.isSlideshow(displayAttributes.value)
})

const isPadletWall = computed(() => {
  return displayAttributes.value && LinkHelpers.isPadletWall(displayAttributes.value)
})

const isPdf = computed(() => {
  return displayAttributes.value?.content_subcategory === 'pdf'
})

const isFacebookPost = computed(() => {
  return displayAttributes.value?.content_category === 'page' && lowerCaseProviderName.value === 'facebook'
})

const isInstagramPost = computed(() => {
  return displayAttributes.value?.content_category === 'page' && lowerCaseProviderName.value === 'instagram'
})

const isTweet = computed(() => {
  return hasTweetAttachment(props.post.wish_content?.attachment_props)
})

const alternativeText = computed(() => {
  return props.post.wish_content?.attachment_props?.alternative_text
})

const xAlternativeText = computed(() => {
  return isImage.value && alternativeText.value
})

const attachmentProps = computed(() => {
  return getAttachmentPropsFromPost(props.post.cid)
})

const tweetWidth = computed(() => {
  const maxWidth = isSmallerThanDesktop() ? 540 : 720
  return Math.min(props.containerWidth, maxWidth)
})

const googleDocsDisplayAttributes = computed(() => {
  const imageDimensions = fallbackAttachmentSizeMapping[FallbackAttachmentType.File]
  return {
    ...displayAttributes.value,
    original_image_width: imageDimensions.width,
    original_image_height: imageDimensions.height,
  } as BeethovenDisplayAttributes
})

const fallbackAttachmentType = computed(() => {
  if (isPadletSlideshow.value) return FallbackAttachmentType.Link
  if (isPadletWall.value) return FallbackAttachmentType.BrokenPadletLink
  if (isGoogleDoc.value) return FallbackAttachmentType.GoogleDocument
  return FallbackAttachmentType.BrokenLink
})

const imageUrlWithFallback = computed(() => {
  if (displayAttributes.value && displayAttributes.value.is_fallback) {
    return fallbackImageUrl(fallbackAttachmentType.value, 'light', null)
  }
  return displayAttributes.value && displayAttributes.value.image_url
})

// =========================
// POLL
// =========================
const poll = computed(() => {
  if (props.post.wish_content?.attachment_props?.type === 'poll') {
    return props.post.wish_content.attachment_props as Poll
  }
  return null
})
const pollContainer = ref<HTMLElement>()
const pollContent = ref() // TODO: type asynchronous component better

const resizePoll = () => {
  nextTick(() => {
    if (pollContainer.value == null || pollContent.value?.$el == null) return

    const pollContainerEl = pollContainer.value
    const pollContentEl = pollContent.value.$el as HTMLElement

    if (!isSmallerThanDesktopBig()) {
      resizeElementToFitParentWithScale(pollContentEl, pollContainerEl, pollSlideshowResizeScales())
      return
    }

    // if poll is resized to smaller than desktop big viewport, we reset the transform and height
    pollContainerEl.style.height = undefined as any
    pollContentEl.style.height = 'fit-content'
    pollContentEl.style.transform = `scale(1)`

    // after that, we check if the poll is overflowing the container
    // if it is overflowing, we set the poll content height to the attachment container height, and the poll container height will be determined by the poll content height
    if (isElementOrChildOverflowingParent(pollContentEl, pollContainerEl) && pollContainerEl.parentElement) {
      const attachmentContainerHeight = pollContainerEl.parentElement.clientHeight
      pollContentEl.style.height = `${attachmentContainerHeight}px`
    }
  })
}

const handleAttachmentLoaded = () => {
  addPostCidToLoadedAttachments(props.post.cid)
  if (poll.value) {
    resizePoll()
  }
}

// =========================
// GIF
// =========================
const isGif = computed(() => displayAttributes.value?.content_subcategory === 'gif')
// #region Image Background Color
const { getDisplayedColorForPost } = useSurfacePostPropertiesStore()
const displayedPostColor = computed((): PostColor | null => getDisplayedColorForPost(props.post))
const backgroundColor = computed(() => postColorRgb({ postColor: displayedPostColor.value, isLightColorScheme: true }))
const attachmentColor = computed(() => displayAttributes.value?.image_color)

const imageBackgroundColor = computed(() => {
  if (!attachmentColor.value) return backgroundColor.value
  return serializeRgbaStringToRgba(attachmentColor.value)
})

const imageBackgroundColorString = computed(() => {
  const c = imageBackgroundColor.value
  return `rgb(${c.r},${c.g},${c.b})`
})
// #endregion

onMounted(() => {
  updateDisplayAttributes()
  if (poll.value) {
    window.addEventListener('resize', resizePoll)
  }
})

onBeforeUnmount(() => {
  if (poll.value) {
    window.removeEventListener('resize', resizePoll)
  }
})
</script>

<template>
  <div
    v-if="poll"
    id="pollContainer"
    ref="pollContainer"
    :class="['w-full h-full flex flex-col items-center justify-center m-8']"
  >
    <SurfacePostPoll
      id="pollContent"
      ref="pollContent"
      :poll="poll"
      :post="{ ...post, color: 'default' }"
      :user="user"
      :class="'h-full tablet-portrait:h-auto max-w-[540px]'"
      :dark-mode="false"
      :display-mode="'slideshow'"
      @load="handleAttachmentLoaded"
    />
  </div>
  <SurfaceAttachmentContentBlocked
    v-else-if="hasBlockedContent && displayAttributes"
    :provider-name="displayAttributes.provider_name"
    @load="handleAttachmentLoaded"
  />
  <SurfaceSlideshowAttachmentImage
    v-else-if="displayAttributes && (isImage || (isPdf && isScreenshotMode))"
    :class="['box-border', 'px-4 tablet-portrait:px-6', isFullPage ? 'desktop-2xl:px-10' : 'tablet-landscape:px-0']"
    :title="xAlternativeText ? alternativeText : displayAttributes.title"
    :url="displayAttributes.url"
    :original-image-url="displayAttributes.original_image_url || displayAttributes.url"
    :original-image-width="displayAttributes.original_image_width || displayAttributes.image_width"
    :original-image-height="displayAttributes.original_image_height || displayAttributes.image_height"
    :is-gif="isGif"
    :background-color="imageBackgroundColorString"
    :image-luminance="displayAttributes.image_luminance"
    @load="handleAttachmentLoaded"
  />
  <div
    v-else-if="
      displayAttributes && (isFile || isPadletWall || isPadletSlideshow || isGoogleDoc || !displayAttributes.embed_code)
    "
    :class="[
      'box-border',
      'h-full w-full',
      'flex justify-center items-center',
      'px-4 tablet-portrait:px-48',
      isFullPage ? 'tablet-landscape:px-80 desktop:px-117 desktop-2xl:px-188' : 'tablet-landscape:px-0',
    ]"
  >
    <SurfaceSlideshowCardFile v-if="isFile" :display-attributes="displayAttributes" @load="handleAttachmentLoaded" />
    <SurfaceSlideshowCardWall
      v-else-if="isPadletWall"
      :original-image-width="displayAttributes.original_image_width || displayAttributes.image_width"
      :original-image-height="displayAttributes.original_image_height || displayAttributes.image_height"
      :title="displayAttributes.title"
      :builder-name="displayAttributes.provider_name"
      :image-src="imageUrlWithFallback"
      :href="displayAttributes.url"
      @load="handleAttachmentLoaded"
    />
    <SurfaceSlideshowCardWall
      v-else-if="isPadletSlideshow"
      :original-image-width="displayAttributes.original_image_width || displayAttributes.image_width"
      :original-image-height="displayAttributes.original_image_height || displayAttributes.image_height"
      :title="`${__('Slideshow')} | ${displayAttributes.title}`"
      :builder-name="displayAttributes.provider_name"
      :image-src="imageUrlWithFallback"
      :href="displayAttributes.url"
      @load="handleAttachmentLoaded"
    />
    <!-- To be revisited in https://linear.app/padlet/issue/PRO-5232/handle-public-google-docs-card -->
    <SurfaceSlideshowCardLink
      v-else-if="isGoogleDoc"
      :display-attributes="googleDocsDisplayAttributes"
      :image-src="imageUrlWithFallback"
      @load="handleAttachmentLoaded"
    />
    <SurfaceSlideshowCardLink v-else :display-attributes="displayAttributes" @load="handleAttachmentLoaded" />
  </div>
  <SurfaceSlideshowAttachmentAudio
    v-else-if="displayAttributes && isAudio"
    :key="forceBackgroundReloadKey"
    :post-cid="post.cid"
    :display-attributes="displayAttributes"
    :attachment-props="attachmentProps"
    :is-full-page="isFullPage"
    :x-custom-thumbnail="!!customThumbnailUrl"
    :embed-code="displayAttributes.embed_code"
    :embed-width="displayAttributes.embed_width"
    :embed-height="displayAttributes.embed_height"
    :post-color="post.color"
    @load="handleAttachmentLoaded"
  />
  <SurfaceSlideshowAttachmentTweet
    v-else-if="isTweet && displayAttributes && displayAttributes.embed_code"
    :attachment-props="attachmentProps"
    :width="tweetWidth"
    :dark-mode="darkMode"
    :url="displayAttributes.url"
    @load="handleAttachmentLoaded"
  />

  <!-- NOTE -->
  <!-- We don't preload video/embed to avoid the case where it starts playing audio in the background. -->
  <!-- There are too many different cases to consider since we allow users to add anything as an attachment. -->
  <!-- Also, iframes generally don't load until they are visible anyway so there's little benefit to preloading them. -->

  <div
    v-else-if="isCurrentlyViewed && displayAttributes && (isVideo || isTiktokVideo)"
    :class="[
      'box-border',
      'w-full',
      'px-4 tablet-portrait:px-6',
      isFullPage ? 'desktop-2xl:px-10' : 'tablet-landscape:px-0',
      videoAttachmentContainerHeight,
    ]"
  >
    <SurfaceSlideshowAttachmentVideo
      :embed-code="displayAttributes.embed_code"
      :embed-width="displayAttributes.embed_width"
      :embed-height="displayAttributes.embed_height"
      :is-upload="isUpload"
      :is-youtube="isYoutube"
      :disable-autoplay="true"
      :should-fill-available-space="isResizableVideo"
      @load="handleAttachmentLoaded"
    />
  </div>
  <!-- Needs to come last since embed_code is not exclusive to SurfacePanelPostExpandedAttachmentEmbed -->
  <div
    v-else-if="isCurrentlyViewed && displayAttributes && displayAttributes.embed_code"
    :class="[
      'box-border',
      'h-full',
      'w-full',
      'px-4 tablet-portrait:px-6',
      isFullPage ? 'desktop-2xl:px-10' : 'tablet-landscape:px-0',
    ]"
  >
    <SurfaceSlideshowAttachmentEmbed
      :embed-code="displayAttributes.embed_code"
      :is-document="isDocument"
      :is-article="isArticle"
      :is-html="isHtml"
      :is-facebook-post="isFacebookPost"
      :is-instagram-post="isInstagramPost"
      :is-tiktok-embed="!isTiktokVideo"
      @load="handleAttachmentLoaded"
    />
  </div>
</template>
